import useApi from 'assets/hooks/api/useApi';
import usePageRouter from 'assets/hooks/pageRouter/usePageRouter';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { useDataProvider } from 'assets/providers/data/DataProvider';
import { cleanValues } from 'assets/utils/data/Object';
import { compact, flatten, reduce, sortBy, uniq, uniqBy } from 'lodash';
import customPricePointDeleteApi from 'models/productManagement/customPricePoint/delete/Api.CustomPricePoint.Delete';
import customPricePointSaveApi from 'models/productManagement/customPricePoint/save/Api.CustomPricePoint.Save';
import Product from 'models/productManagement/product/Model.Product';
import useDistributorPricingDistributions from 'module/productManagement/hooks/useDistributorPricingDistributions';
import { useEffect, useMemo, useState } from 'react';
import { customPricingRoute } from './CustomPricing.Index';
import useDealerPricingSettings from 'module/productManagement/hooks/useDealerPricingSettings';
import iterateWithProgress, { ProgressInfo } from 'assets/utils/data/Iterations';
import { ProgressKey } from 'assets/components/progressWindow/ProgressWindow';
import ProductCustomPricePoint, {
  productCustomPricePointDeleteApi,
} from 'models/productManagement/productCustomPricePoint/Model.ProductCustomPricePoint';
import { OrderType } from 'config/Api.Config';
import CustomPricePoint from 'models/productManagement/customPricePoint/Model.CustomPricePoint';

type Term = number;
type CustomInfo = string;
type CustomInfoGroup = number;
type PricePoint = Model.ICustomPricePoint & {
  isUpdated?: boolean;
};
export default function useCustomPricing() {
  const { permissions, isDistributor } = useAuthStore();
  const pageRouter = usePageRouter<
    Module.ProductManagement.CustomPricing.Params,
    Module.ProductManagement.CustomPricing.Query
  >({
    route: customPricingRoute,
  });
  const productId = parseInt(pageRouter.params.productId) || 0;
  const productProvinceGroupId = parseInt(pageRouter.params.productProvinceGroupId) || undefined;
  const hasAccess =
    permissions.VIEW_CUSTOMPRICEPOINT ||
    permissions.CREATE_CUSTOMPRICEPOINT ||
    permissions.EDIT_CUSTOMPRICEPOINT ||
    permissions.DELETE_CUSTOMPRICEPOINT;

  const listApi = useApi(
    {
      action: ProductCustomPricePoint.list,
      body: {
        productId: [productId],
        groupId: productProvinceGroupId ? [productProvinceGroupId] : undefined,
        orderBy: 'CustomInfoGroup',
        orderType: OrderType.desc,
        thenBy: ['MinPurchasePriceCAD', 'MaxPurchasePriceCAD', 'MinFinancedAmountCAD', 'MaxFinancedAmountCAD'],
        thenType: [OrderType.asc, OrderType.asc, OrderType.asc, OrderType.asc],
      },
      wait: !hasAccess,
    },
    [productId, hasAccess, productProvinceGroupId]
  );

  const productApi = useApi({
    action: Product.find,
    body: { id: productId },
    wait: !hasAccess,
  });

  const distributorPricingDistributions = useDistributorPricingDistributions({
    productId,
  });

  const dealerPricingSettings = useDealerPricingSettings({
    productId,
  });

  //#region PRICES
  const [data, setData] = useState<PricePoint[]>([]);
  const isUpdated = useMemo(
    () =>
      data?.some((it) => it.isUpdated) ||
      distributorPricingDistributions.distributorData?.isUpdated ||
      distributorPricingDistributions.distributorData?.distributions?.some((it) => it.isUpdated) ||
      (dealerPricingSettings.isUpdated && !dealerPricingSettings.hasErrors),
    [
      data,
      distributorPricingDistributions.distributorData,
      dealerPricingSettings.isUpdated,
      dealerPricingSettings.hasErrors,
    ]
  );
  const [isSaving, setIsSaving] = useState(false);

  const pricePointListApi = useApi({
    action: CustomPricePoint.list,
    body: { productId: [productId] },
    wait: !hasAccess,
  });
  useEffect(() => {
    const groupIds = listApi.payload?.data?.filter((it) => it.productProvinceGroupId == productProvinceGroupId);
    const pricePoints = pricePointListApi?.payload?.data?.filter((it) =>
      groupIds?.some((g) => g.id === it.productCustomPricePointId)
    );
    setData(pricePoints ?? []);
  }, [listApi.payload?.data, pricePointListApi?.payload?.data, productProvinceGroupId]);

  async function saveAll(onSaveProgress?: (key: ProgressKey, progressInfo: ProgressInfo) => void) {
    setIsSaving(true);
    distributorPricingDistributions.saveAll(onSaveProgress);
    dealerPricingSettings.saveAll(onSaveProgress);
    const updatedPricesCount = await iterateWithProgress(
      data.filter((it) => it.isUpdated),
      async (pricePoint) => {
        if (pricePoint.priceCAD == null && pricePoint.id) {
          await customPricePointDeleteApi(pricePoint.id);
        } else if (pricePoint.priceCAD != null) {
          await customPricePointSaveApi(cleanValues(pricePoint));
        }
      },
      (p) => onSaveProgress('priceDistribution', p)
    );
    if (updatedPricesCount) {
      await reload();
    }
    setIsSaving(false);
  }

  async function reload() {
    await Promise.all([listApi.execute((body) => body), pricePointListApi.execute((body) => body)]);
  }

  function getPrice(termInMonths: Term, customInfo: string, productCustomPricePointId: number) {
    return data.find(
      (it) =>
        it.termInMonths === termInMonths &&
        it.customInfo === customInfo &&
        it.productCustomPricePointId === productCustomPricePointId
    );
  }
  function updatePrice(
    termInMonths: Term,
    customInfo: string,
    productCustomPricePointId: number,
    pricingDistributions?: Partial<Utils.PricingDistribution>
  ) {
    const d = getPrice(termInMonths, customInfo, productCustomPricePointId);
    if (d) {
      d.isUpdated = true;
      if (pricingDistributions == null) {
        d.priceCAD = null;
        d.pricingDistributions = null;
        d.subfees = null;
      } else {
        d.pricingDistributions = {
          ...productApi?.payload?.pricingDistribution,
          ...d.pricingDistributions,
          ...pricingDistributions,
        };
        d.priceCAD = reduce(d.pricingDistributions || {}, (prev, curr) => prev + curr.amount, 0);
      }
      setData((old) => [...old]);
    } else {
      const newD: PricePoint = {
        id: undefined,
        isUpdated: true,
        termInMonths,
        customInfo,
        productCustomPricePointId,
        priceCAD: reduce(pricingDistributions || {}, (prev, curr) => prev + curr.amount, 0),
        pricingDistributions,
      };
      setData((old) => [...old, newD]);
    }
  }
  function updateSubfee(
    termInMonths: Term,
    customInfo: string,
    productCustomPricePointId: number,
    subfees?: Utils.SurchargeInfo
  ) {
    const d = getPrice(termInMonths, customInfo, productCustomPricePointId);
    if (d) {
      d.isUpdated = true;
      d.subfees = uniqBy(compact([subfees, ...(d.subfees ?? [])]), (it) => it.subfeeId);
      setData((old) => [...old]);
    }
  }
  //#endregion

  //#region TERMS
  const terms = useMemo(() => sortBy(uniq(data.map((it) => it.termInMonths))), [data]);

  function removeTerm(termInMonths: Term) {
    setData((old) => old.filter((it) => it.termInMonths === termInMonths));
  }
  //#endregion

  //#region LIMITS
  const customInfoList = useMemo(() => sortBy(uniq(data?.map((it) => it.customInfo))), [data]);
  const productCustomPricePointList = useMemo(
    () => listApi.payload?.data?.filter((it) => it.productProvinceGroupId == productProvinceGroupId) || [],
    [listApi.payload?.data, productProvinceGroupId]
  );

  function addEmptyPrices(
    newTerms: number[],
    newCustomInfos: CustomInfo[],
    newProductCustomPricePointIds: CustomInfoGroup[]
  ) {
    const prices: PricePoint[] = [];
    for (const productCustomPricePointId of newProductCustomPricePointIds) {
      for (const termInMonths of newTerms) {
        for (const customInfo of newCustomInfos) {
          if (
            !data.some(
              (it) =>
                it.termInMonths === termInMonths &&
                it.customInfo === customInfo &&
                it.productCustomPricePointId === productCustomPricePointId
            )
          ) {
            prices.push({
              id: undefined,
              isUpdated: false,
              termInMonths,
              customInfo,
              productCustomPricePointId,
              priceCAD: null,
            });
          }
        }
      }
    }
    setData((old) => [...old, ...prices]);
  }

  function removeCustomInfo(customInfo: string) {
    setData((old) => old.filter((it) => !(it.customInfo === customInfo)));
  }

  async function removeCustomInfoGroup(productCustomPricePointId: number) {
    setIsSaving(true);
    setData((old) => old.filter((it) => !(it.productCustomPricePointId === productCustomPricePointId)));
    await productCustomPricePointDeleteApi({ id: productCustomPricePointId });
    await reload();
    setIsSaving(false);
  }
  //#endregion

  const isAllowedToCopy = !!productProvinceGroupId && data.length === 0;
  async function copy() {
    setIsSaving(true);
    if (!listApi.payload?.data?.some((it) => it.productProvinceGroupId === productProvinceGroupId)) {
      const defaultCustomPricingPoints = flatten(
        listApi.payload?.data?.filter((it) => !it.productProvinceGroupId).map((it) => it.customPricePoints)
      );
      for (const customPricingPoint of defaultCustomPricingPoints || []) {
        await customPricePointSaveApi({
          termInMonths: customPricingPoint.termInMonths,
          priceCAD: customPricingPoint.priceCAD,
          customInfo: customPricingPoint.customInfo,
          productCustomPricePointId: customPricingPoint.productCustomPricePointId,
          pricingDistributions: customPricingPoint.pricingDistributions,
        });
      }
    }
    await reload();
    setIsSaving(false);
  }
  const [editedId, setEditableId] = useState<number | null>(null);

  return {
    editedId,
    setEditableId,
    productId,
    isDistributor,
    distributorPricingDistributions,
    dealerPricingSettings,
    provinceGroup: productApi?.payload?.productProvinceGroups?.find((g) => g.id === productProvinceGroupId),
    hasAccess,
    isAllowedToCopy,
    copy,
    isSaving,
    isUpdated,
    addEmptyPrices,
    removeTerm,
    removeCustomInfo,
    removeCustomInfoGroup,
    updatePrice,
    updateSubfee,
    getPrice,
    data,
    terms,
    customInfoList,
    productCustomPricePointList,
    saveAll,
    pageRouter,
    productApi,
    listApi,
    permissions,
  };
}

export function useCustomPricingProvider() {
  return useDataProvider<ReturnType<typeof useCustomPricing>>();
}
